/**
 * Adding a volunteer by hand (M3).
 *
 * The API has accepted `POST /volunteers` since M1 and the portal never called
 * it — an M1 gap that only became load-bearing in M3, because a **corporate**
 * volunteer is staff-entered by definition. Without this form the only way to
 * record one was `curl`.
 *
 * **It asks what the public registration form asks.** It used to ask six
 * fields — name, contact, city, category — and leave the other twenty to the
 * profile editor, on the theory that staff would fill them in later. They did
 * not, so a volunteer entered by hand had no age band, no state, no interests
 * and no frequency, and every report that split on those quietly counted them
 * as unknown. Both forms now build from `constants/volunteerFields`, so a
 * question added to one is asked by the other.
 *
 * **Every field on it is required** (Parinaam, 22 Sep 2026).
 *
 * This reversed the rule that stood here before, and the reversal is worth
 * recording rather than quietly overwriting. The argument for optional fields
 * was that staff taking a name at the end of a session have a first name and a
 * number, and a form refusing that is a form they will work around. Parinaam's
 * answer was that the half-filled records are the problem being reported: a
 * volunteer with no age band, no city and no interests cannot be rostered by
 * anything except memory, and the profile editor that was meant to catch up
 * later never did. A member of staff typing this form has the person in front of
 * them or on the phone, which is the one moment the details can actually be
 * asked for.
 *
 * **What "every field" means precisely: every field the form draws.** The checks
 * in `submit` follow what is on the screen, not what the column permits — the
 * organization picker is required only for a corporate volunteer, the
 * institution only for a student, the occupation only for an individual, and the
 * three "please specify" boxes only once the answer revealing them is chosen.
 * Nothing hidden is ever demanded.
 *
 * **The API is deliberately not changed to match.** `createVolunteerSchema`
 * still accepts a volunteer with far less, and backend tests pin that down: the
 * public registration form and the corporate Excel import both create volunteers
 * through paths that cannot ask a person for anything. What the API tolerates and
 * what this form should let somebody type are not the same question.
 *
 * Two of the requirements need their own reasons.
 *
 *   **Mandatory training.** The activation gate refuses anybody without a
 *   current training date, so a volunteer added without one could be created and
 *   then immediately not be activated. Asking at the point of entry turns that
 *   later 409 into a field on the form. Somebody who genuinely has not trained
 *   yet arrives through the import or the public form, neither of which comes
 *   through here.
 *
 *   **"Anything else worth recording?"** — a free-text box, and the one field
 *   where "required" is a judgement rather than an obvious win. It is included
 *   because Parinaam asked for every field, and it is flagged here because it is
 *   the first one to revisit if staff start typing "n/a" to get past it.
 */

import { useState } from 'react';
import {
  categoryForVolunteerType,
  joinCodes,
  OCCUPATION,
  VOLUNTEER_TYPE,
  VOLUNTEERING_FREQUENCY,
} from '@parinaam-vms/shared';
import type { VolunteeringFrequency, VolunteerType } from '@parinaam-vms/shared';
import {
  Button,
  CheckboxGroup,
  ErrorState,
  LoadingState,
  RadioGroup,
  SelectInput,
  TextArea,
  TextInput,
} from '../../components/common';
import { OrganizationPicker } from '../../components/organizations/OrganizationPicker';
import { INDIA_STATES, OTHER_CITY, citiesForState } from '../../constants/indiaLocations';
import {
  AGE_GROUP_OPTIONS,
  FREQUENCY_OPTIONS,
  OCCUPATION_OPTIONS,
  VOLUNTEER_TYPE_OPTIONS,
  toReferenceOptions,
} from '../../constants/volunteerFields';
import { useReferenceValuesGrouped } from '../../hooks/useReferenceValues';
import { useCreateVolunteer } from '../../hooks/useVolunteers';
import { ApiRequestError } from '../../services/api/client';
import { contactProblems } from '../../utils/contact';
import styles from './AddVolunteerForm.module.css';

export interface AddVolunteerFormProps {
  onClose: () => void;
}

/** Strings throughout, because that is what inputs hold. */
interface FormState {
  kind: string;
  organization_id: string;
  institution: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  state: string;
  city_choice: string;
  city_other: string;
  age_group: string;
  gender: string;
  occupation: string;
  occupation_other: string;
  referral_source: string;
  areas_of_interest: string[];
  volunteering_frequency: string;
  volunteering_frequency_other: string;
  skills: string;
  training_completed_on: string;
}

const EMPTY_FORM: FormState = {
  kind: VOLUNTEER_TYPE.INDIVIDUAL,
  organization_id: '',
  institution: '',
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  state: '',
  city_choice: '',
  city_other: '',
  age_group: '',
  gender: '',
  occupation: '',
  occupation_other: '',
  referral_source: '',
  areas_of_interest: [],
  volunteering_frequency: '',
  volunteering_frequency_other: '',
  skills: '',
  training_completed_on: '',
};

/** Today, as `YYYY-MM-DD` — a training taken tomorrow has not been taken. */
function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Every answer this form is still waiting for, keyed as the API keys them.
 *
 * Lifted out of the submit handler on 9 Oct 2026 so the render can ask the
 * same question the submit does: Parinaam asked for the button to stay
 * disabled until the form is complete, and a rule that only runs on click
 * cannot tell anybody whether it is.
 *
 * Pure, and takes the whole form rather than the pieces, so there is exactly
 * one definition of "complete" — a second copy written for the button would
 * disagree with this one the first time a field was added.
 */
/**
 * What each outstanding answer is called, for the line beside the button.
 *
 * Needed because disabling the button takes something away: the inline field
 * errors only appear on submit, and a submit that cannot happen never shows
 * them. Without this, somebody with one empty box in a form this long is told
 * only that one answer is missing, and has to hunt for it.
 *
 * The visitor's words, not the column's (§8.4.4) — and deliberately shorter
 * than the field labels, because this is a list rather than a form.
 */
const ANSWER_LABELS: Record<string, string> = {
  first_name: 'first name',
  last_name: 'last name',
  organization_id: 'organisation',
  institution: 'where they study',
  email: 'email address',
  phone: 'phone number',
  age_group: 'age group',
  gender: 'gender',
  state: 'state',
  city_choice: 'city',
  city_other: 'city name',
  referral_source: 'how they heard about Parinaam',
  occupation: 'occupation',
  occupation_other: 'what they do',
  areas_of_interest: 'areas of interest',
  volunteering_frequency: 'how often',
  volunteering_frequency_other: 'how often',
  skills: 'anything else worth recording',
  training_completed_on: 'training date',
};

function missingAnswers(form: FormState): Record<string, string> {
  const isCorporate = form.kind === VOLUNTEER_TYPE.CORPORATE;
  const isStudent = form.kind === VOLUNTEER_TYPE.STUDENT;
  const isIndividual = form.kind === VOLUNTEER_TYPE.INDIVIDUAL;


  const problems: Record<string, string> = {};

  // Every field this form shows, required (Parinaam, 22 Sep 2026). The rule is
  // "whatever is on the screen has to be answered", so the checks below track
  // what is *rendered* rather than what the column allows: a field hidden for
  // this kind of volunteer is not asked for, and one revealed by an answer
  // becomes required the moment it appears.
  if (!form.first_name.trim()) {
    problems.first_name = 'Enter a first name.';
  }
  if (!form.last_name.trim()) {
    problems.last_name = 'Enter a last name.';
  }
  if (isCorporate && !form.organization_id) {
    problems.organization_id = 'Choose the organisation this group belongs to.';
  }
  if (isStudent && !form.institution.trim()) {
    problems.institution = 'Say where they study.';
  }
  // Both mandatory whatever the kind. Same check the profile editor runs
  // (`utils/contact`), and the same one `createVolunteerSchema` applies after.
  Object.assign(problems, contactProblems(form.email, form.phone));

  if (!form.age_group) {
    problems.age_group = 'Choose an age group.';
  }
  if (!form.gender) {
    problems.gender = 'Choose a gender.';
  }
  if (!form.state) {
    problems.state = 'Choose a state.';
  }
  if (!form.city_choice) {
    problems.city_choice = 'Choose a city.';
  }
  if (!form.referral_source) {
    problems.referral_source = 'Say how they heard about Parinaam.';
  }
  if (isIndividual && !form.occupation) {
    problems.occupation = 'Choose an occupation.';
  }
  if (form.areas_of_interest.length === 0) {
    problems.areas_of_interest = 'Choose at least one.';
  }
  if (!form.volunteering_frequency) {
    problems.volunteering_frequency = 'Choose how often they would like to volunteer.';
  }
  if (!form.skills.trim()) {
    problems.skills = 'Enter something here.';
  }
  if (!form.training_completed_on) {
    problems.training_completed_on = 'Enter the date they completed the mandatory training.';
  }

  // The three conditional boxes: each is required only while the answer that
  // reveals it is chosen, exactly as on the public form.
  if (form.city_choice === OTHER_CITY && !form.city_other.trim()) {
    problems.city_other = 'Enter the city.';
  }
  if (form.occupation === OCCUPATION.OTHER && !form.occupation_other.trim()) {
    problems.occupation_other = 'Say what they do.';
  }
  if (
    form.volunteering_frequency === VOLUNTEERING_FREQUENCY.OTHER &&
    !form.volunteering_frequency_other.trim()
  ) {
    problems.volunteering_frequency_other = 'Say how often.';
  }

  return problems;
}

export function AddVolunteerForm({ onClose }: AddVolunteerFormProps) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const reference = useReferenceValuesGrouped();
  const create = useCreateVolunteer();

  const set =
    <K extends keyof FormState>(key: K) =>
    (value: FormState[K]) => {
      setForm((current) => ({ ...current, [key]: value }));
      // Clear the field's error as soon as it is touched: a red message under a
      // box somebody is actively fixing reads as a second failure.
      setErrors((current) => (key in current ? { ...current, [key]: '' } : current));
    };

  /*
   * Changing the kind clears whatever belonged to the old one. A student's
   * institution left behind on a corporate record, or an organisation left on an
   * individual, is refused by the API and by a CHECK constraint, and the person
   * entering it would not be told why.
   */
  const setKind = (value: string) => {
    setForm((current) => ({
      ...current,
      kind: value,
      organization_id: value === VOLUNTEER_TYPE.CORPORATE ? current.organization_id : '',
      institution: value === VOLUNTEER_TYPE.STUDENT ? current.institution : '',
      occupation: value === VOLUNTEER_TYPE.INDIVIDUAL ? current.occupation : '',
      occupation_other: value === VOLUNTEER_TYPE.INDIVIDUAL ? current.occupation_other : '',
    }));
    setErrors({});
  };

  const isCorporate = form.kind === VOLUNTEER_TYPE.CORPORATE;
  const isStudent = form.kind === VOLUNTEER_TYPE.STUDENT;
  const isIndividual = form.kind === VOLUNTEER_TYPE.INDIVIDUAL;

  /*
   * What is still outstanding, recomputed as the form is filled in.
   *
   * The same function the submit runs, so the button and the validation cannot
   * disagree about whether the form is complete.
   */
  const outstanding = missingAnswers(form);
  const incomplete = Object.keys(outstanding).length > 0;
  /*
   * De-duplicated: the two "how often" boxes are one question to the person
   * filling the form, and naming it twice reads as a bug.
   */
  const outstandingLabels = [
    ...new Set(Object.keys(outstanding).map((key) => ANSWER_LABELS[key] ?? key)),
  ];

  const submit = () => {
    const problems = missingAnswers(form);

    if (Object.keys(problems).length > 0) {
      setErrors(problems);
      return;
    }

    const text = (value: string) => (value.trim() === '' ? null : value.trim());

    create.mutate(
      {
        first_name: form.first_name.trim(),
        // Plenty of people have no surname, and a group's name goes in a single
        // field — there is no "last name" of a school.
        last_name: text(form.last_name),
        email: form.email.trim(),
        phone: form.phone.trim(),
        category: categoryForVolunteerType(form.kind as VolunteerType),
        organization_id: isCorporate ? form.organization_id : null,
        institution: isStudent ? text(form.institution) : null,
        state: text(form.state),
        city: form.city_choice === OTHER_CITY ? text(form.city_other) : text(form.city_choice),
        age_group: text(form.age_group),
        gender: text(form.gender),
        occupation: isIndividual ? text(form.occupation) : null,
        // Only OTHER carries a specified detail, so a code somebody changed
        // their mind about leaves nothing behind.
        occupation_other:
          isIndividual && form.occupation === OCCUPATION.OTHER ? text(form.occupation_other) : null,
        referral_source: text(form.referral_source),
        areas_of_interest: joinCodes(form.areas_of_interest),
        // The radio group can only hold one of the five codes or '', so the
        // cast is narrowing to what the control already guarantees.
        volunteering_frequency: text(form.volunteering_frequency) as VolunteeringFrequency | null,
        volunteering_frequency_other:
          form.volunteering_frequency === VOLUNTEERING_FREQUENCY.OTHER
            ? text(form.volunteering_frequency_other)
            : null,
        skills: text(form.skills),
        training_completed_on: text(form.training_completed_on),
      },
      {
        onSuccess: onClose,
        onError: (failure) => {
          // The API refuses a channel with no detail behind it (D15) and a group
          // with no organisation. Both come back as field errors.
          if (failure instanceof ApiRequestError && failure.isValidation) {
            const fields =
              (failure.details as { field: string; message: string }[] | undefined) ?? [];
            setErrors(Object.fromEntries(fields.map((f) => [f.field, f.message])));
          }
        },
      }
    );
  };

  if (reference.isPending) {
    return (
      <div className={styles.form}>
        <LoadingState label="Loading the form…" />
      </div>
    );
  }

  if (reference.isError) {
    return (
      <div className={styles.form}>
        <ErrorState error={reference.error} onRetry={() => void reference.refetch()} />
      </div>
    );
  }

  const options = toReferenceOptions(reference.data);
  const generalError =
    create.isError && Object.keys(errors).length === 0 ? (create.error as Error).message : null;

  return (
    <div className={styles.form}>
      <h2 className={styles.title}>Add a volunteer</h2>

      {generalError && (
        <p className={styles.error} role="alert">
          {generalError}
        </p>
      )}

      <fieldset className={styles.section} disabled={create.isPending}>
        <legend className={styles.sectionTitle}>How are they volunteering?</legend>

        <RadioGroup
          label="Are they volunteering on their own, through their organisation, or as a student?"
          required
          // Sentences, not terms — they are wider than any sensible grid track,
          // so they go down the page.
          layout="stack"
          options={VOLUNTEER_TYPE_OPTIONS}
          value={form.kind}
          onChange={setKind}
        />

        {/*
          The partner, for an employee volunteering through their company and for
          a group record — which the database refuses without one
          (`volunteers_group_has_organization`). Never a free-text name: that is
          how `Infosys`, `infosys` and `Infosys Ltd` pile up.
        */}
        {isCorporate && (
          <OrganizationPicker
            value={form.organization_id}
            required
            error={errors.organization_id}
            onChange={set('organization_id')}
          />
        )}

        {/* A pick-list for the same reason: three spellings of one college are
            three institutions as far as any report is concerned. */}
        {isStudent && (
          <SelectInput
            label="Where do they study?"
            required
            placeholder="Choose an institution"
            options={options.institutions}
            value={form.institution}
            error={errors.institution}
            onChange={(event) => set('institution')(event.target.value)}
          />
        )}
      </fieldset>

      <fieldset className={styles.section} disabled={create.isPending}>
        <legend className={styles.sectionTitle}>About them</legend>

        <div className={styles.row}>
          <TextInput
            label="First name"
            required
            value={form.first_name}
            error={errors.first_name}
            onChange={(event) => set('first_name')(event.target.value)}
          />
          <TextInput
            label="Last name"
            required
            value={form.last_name}
            error={errors.last_name}
            onChange={(event) => set('last_name')(event.target.value)}
          />
        </div>

        <div className={styles.row}>
          <TextInput
            label="Email address"
            type="email"
            required
            value={form.email}
            error={errors.email}
            onChange={(event) => set('email')(event.target.value)}
          />
          {/*
            Not the public form's strict ten digits. A member of staff recording
            a landline with an STD code, or an international number for a
            corporate contact, is not the visitor typing their own mobile — the
            shared schema is lenient here for exactly that reason.
          */}
          <TextInput
            label="Phone number"
            type="tel"
            required
            value={form.phone}
            error={errors.phone}
            onChange={(event) => set('phone')(event.target.value)}
          />
        </div>

        <div className={styles.row}>
          <SelectInput
            label="Age group"
            required
            placeholder="Choose an age group"
            options={AGE_GROUP_OPTIONS}
            value={form.age_group}
            error={errors.age_group}
            onChange={(event) => set('age_group')(event.target.value)}
          />
          <SelectInput
            label="Gender"
            required
            placeholder="Choose a gender"
            options={options.gender}
            value={form.gender}
            error={errors.gender}
            onChange={(event) => set('gender')(event.target.value)}
          />
        </div>

        {/*
          State then city, both from the same fixed lists the public form uses,
          so the master is not littered with "Bangalore"/"Bengaluru"/"bengaluru"
          for one place. The city list ends in "Other", which reveals a box.
        */}
        <div className={styles.row}>
          <SelectInput
            label="State"
            required
            placeholder="Choose a state"
            options={INDIA_STATES.map((s) => ({ value: s, label: s }))}
            value={form.state}
            error={errors.state}
            onChange={(event) => {
              const value = event.target.value;
              // Cities depend on the state, so changing it clears the city.
              setForm((current) => ({
                ...current,
                state: value,
                city_choice: '',
                city_other: '',
              }));
              setErrors((current) => ({ ...current, state: '', city_other: '' }));
            }}
          />
          <SelectInput
            label="City"
            required
            disabled={form.state === ''}
            placeholder={form.state === '' ? 'Choose a state first' : 'Choose a city'}
            options={[
              ...citiesForState(form.state).map((c) => ({ value: c, label: c })),
              { value: OTHER_CITY, label: 'Other (not listed)' },
            ]}
            value={form.city_choice}
            error={errors.city_choice}
            onChange={(event) => {
              const value = event.target.value;
              set('city_choice')(value);
              // Leaving "Other" clears the box it revealed.
              if (value !== OTHER_CITY && form.city_other !== '') set('city_other')('');
            }}
          />
        </div>
        {form.city_choice === OTHER_CITY && (
          <TextInput
            label="Enter the city"
            required
            value={form.city_other}
            error={errors.city_other}
            onChange={(event) => set('city_other')(event.target.value)}
          />
        )}

        <SelectInput
          label="How did they hear about Parinaam?"
          required
          placeholder="Choose an answer"
          options={options.referralSources}
          value={form.referral_source}
          error={errors.referral_source}
          onChange={(event) => set('referral_source')(event.target.value)}
        />

        {/* A question about the person: a corporate volunteer answers it through
            their employer and a student through "As a student". */}
        {isIndividual && (
          <>
            <SelectInput
              label="Occupation"
              required
              placeholder="Choose an occupation"
              options={OCCUPATION_OPTIONS}
              value={form.occupation}
              error={errors.occupation}
              onChange={(event) => {
                const value = event.target.value;
                set('occupation')(value);
                if (value !== OCCUPATION.OTHER && form.occupation_other !== '') {
                  set('occupation_other')('');
                }
              }}
            />
            {form.occupation === OCCUPATION.OTHER && (
              <TextInput
                label="Please specify the occupation"
                required
                value={form.occupation_other}
                error={errors.occupation_other}
                onChange={(event) => set('occupation_other')(event.target.value)}
              />
            )}
          </>
        )}
      </fieldset>

      <fieldset className={styles.section} disabled={create.isPending}>
        <legend className={styles.sectionTitle}>How would they like to help?</legend>

        <CheckboxGroup
          label="What would they like to help with?"
          required
          options={options.areasOfInterest}
          value={form.areas_of_interest}
          error={errors.areas_of_interest}
          onChange={set('areas_of_interest')}
        />

        <RadioGroup
          label="How often would they like to volunteer?"
          required
          options={FREQUENCY_OPTIONS}
          value={form.volunteering_frequency}
          error={errors.volunteering_frequency}
          onChange={(value) => {
            set('volunteering_frequency')(value);
            if (
              value !== VOLUNTEERING_FREQUENCY.OTHER &&
              form.volunteering_frequency_other !== ''
            ) {
              set('volunteering_frequency_other')('');
            }
          }}
        />
        {form.volunteering_frequency === VOLUNTEERING_FREQUENCY.OTHER && (
          <TextInput
            label="Please say how often"
            required
            value={form.volunteering_frequency_other}
            error={errors.volunteering_frequency_other}
            onChange={(event) => set('volunteering_frequency_other')(event.target.value)}
          />
        )}

        <TextArea
          label="Anything else worth recording?"
          required
          rows={3}
          value={form.skills}
          error={errors.skills}
          onChange={(event) => set('skills')(event.target.value)}
        />
      </fieldset>

      {/*
        The one question the public form does not ask, and the reason it does not:
        mandatory training moved to the activity sign-up, so a visitor is never
        shown the material. Staff are asked the opposite question — when it was
        last completed — because a volunteer cannot be activated without one that
        is still in date, and the sweep moves a lapsed one to INACTIVE.
      */}
      <fieldset className={styles.section} disabled={create.isPending}>
        <legend className={styles.sectionTitle}>Mandatory training</legend>

        <TextInput
          label="Training completed on"
          type="date"
          required
          max={today()}
          value={form.training_completed_on}
          error={errors.training_completed_on}
          onChange={(event) => set('training_completed_on')(event.target.value)}
        />
      </fieldset>

      <div className={styles.actions}>
        <Button
          variant="primary"
          loading={create.isPending}
          disabled={incomplete}
          onClick={submit}
        >
          Add volunteer
        </Button>
        <Button onClick={onClose} disabled={create.isPending}>
          Cancel
        </Button>

        {/*
          A disabled button with no explanation is a dead end: somebody who
          cannot see which box is empty has nothing to click and nothing to
          read. The count is live, so it falls as they fill the form in, and
          the inline errors still say which field once they have tried once.
        */}
        {incomplete && (
          <p className={styles.outstanding} aria-live="polite">
            Still needed: {outstandingLabels.join(', ')}.
          </p>
        )}
      </div>
    </div>
  );
}
